import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseAndValidateRange, validateRange } from './date-picker-helper';
import { CalendarIconContainer, Container, Input, ResetButton } from './DateInput.styles';
import { usePrevious } from '../../../../play-core/hooks/use-previous';
import SvgCalendar from '../../../../play-core/icons/Calendar';
import SvgClear from '../../../../play-core/icons/Clear';
import SvgError from '../../../../play-core/icons/Error';
import { ignoreTimezoneExtension } from '../../../../play-core/utils/date-helper';
import { isoDate, shortDateFormat } from '../../utils/date-formatter';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const DateInput = ({
  date = '',
  restrictedRange = {},
  requestFocus = false,
  showCalendarIcon = false,
  inputStyle = 'Single',
  showError = false,
  fullWidth = false,
  expand = false,
  onChange,
  onValidityChanged = () => undefined,
  onFocus = () => undefined,
  onBlur = () => undefined,
  isDisabled = false
}) => {
  const {
    t
  } = useTranslation();
  const inputRef = useRef(null);
  const previousDate = usePrevious(date);
  const formattedDate = date && shortDateFormat(date + ignoreTimezoneExtension);
  const [inputValue, setInputValue] = useState(formattedDate);
  useEffect(() => {
    setInputValue(formattedDate);
  }, [formattedDate]);

  // Validate and revalidate the value passed to this component if the date changes between renders.
  useEffect(() => {
    if (date && date !== previousDate) {
      const validity = validateRange(isoDate(date), restrictedRange);
      onValidityChanged(validity);
    }
  }, [date, onValidityChanged, previousDate, restrictedRange]);
  const emitCurrentValue = (forceEmit = false) => {
    const result = parseAndValidateRange(inputValue, restrictedRange);
    onValidityChanged(result.validity);
    if (result.validity === 'Valid') {
      const areDatesEmpty = result.date === undefined && date === '';
      const hasChanged = result.date !== date && !areDatesEmpty;
      if (hasChanged || forceEmit) {
        onChange(result.date);
      }
      if (result.date) {
        setInputValue(shortDateFormat(result.date + ignoreTimezoneExtension));
      }
    }
  };
  useEffect(() => {
    setTimeout(() => {
      const input = inputRef.current;
      if (input) {
        const hasFocus = document.activeElement === input;
        if (requestFocus && !hasFocus) {
          input.focus();
        }
      }
    }, 0);
  }, [requestFocus, inputRef]);
  return /*#__PURE__*/_jsxs(Container, {
    fullWidth: fullWidth,
    expand: expand,
    children: [showCalendarIcon && /*#__PURE__*/_jsx(CalendarIconContainer, {
      children: /*#__PURE__*/_jsx(SvgCalendar, {})
    }), /*#__PURE__*/_jsx(Input, {
      "aria-live": "polite",
      ref: inputRef,
      type: "text",
      placeholder: t('search.datePlaceholder'),
      value: inputValue,
      showError: showError,
      showPrefixIcon: showCalendarIcon,
      hasText: !!inputValue,
      inputStyle: inputStyle,
      onChange: event => setInputValue(event.target.value),
      onKeyDown: event => {
        if (event.key === 'Enter') {
          // Set forceEmit to force an update if the user presses Enter,
          // even if there is still the same value as before.
          emitCurrentValue(true);
        }
      },
      onBlur: () => {
        emitCurrentValue();
        onBlur();
      },
      onFocus: onFocus,
      disabled: isDisabled
    }), inputValue && !isDisabled && /*#__PURE__*/_jsx(ResetButton, {
      "aria-label": t('global.delete'),
      showError: showError,
      tabIndex: -1,
      onClick: () => {
        setInputValue('');
        onChange(undefined);
        onValidityChanged('Valid');
        inputRef.current && inputRef.current.focus();
      },
      children: showError ? /*#__PURE__*/_jsx(SvgError, {}) : /*#__PURE__*/_jsx(SvgClear, {})
    })]
  });
};